import { useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { 
  Sparkles, Loader2, Wand2, FileText, Check, X, 
  RefreshCw, Lightbulb, ClipboardCopy, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const NOTE_TYPES = [
  { value: 'clinical_note', label: 'Clinical Note' },
  { value: 'nursing_note', label: 'Nursing Note' },
  { value: 'social_work_note', label: 'Social Work Note' },
  { value: 'chaplain_note', label: 'Chaplain Note' },
  { value: 'medication_note', label: 'Medication Note' },
  { value: 'symptom_note', label: 'Symptom Note' },
  { value: 'family_communication', label: 'Family Communication' },
  { value: 'care_coordination', label: 'Care Coordination' },
];

const ENHANCEMENT_TYPES = [
  { value: 'improve', label: 'Improve Clarity', icon: Wand2, description: 'Fix grammar and improve flow' },
  { value: 'professional', label: 'Make Professional', icon: FileText, description: 'Use clinical terminology' },
  { value: 'expand', label: 'Expand Details', icon: ArrowRight, description: 'Add more clinical detail' },
  { value: 'summarize', label: 'Summarize', icon: ClipboardCopy, description: 'Create concise summary' },
];

export default function AIDocumentationAssist({ 
  chartId,
  patientName,
  diagnosis,
  currentMedications = [],
  recentSymptoms = [],
  onNoteGenerated,
  buttonVariant = "default",
  buttonSize = "default",
  className = ""
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('suggest'); // suggest, enhance, phrases
  const [isLoading, setIsLoading] = useState(false);
  
  // Suggestion state
  const [noteType, setNoteType] = useState('clinical_note');
  const [visitType, setVisitType] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [suggestedContent, setSuggestedContent] = useState('');
  const [suggestedTitle, setSuggestedTitle] = useState('');
  const [suggestedTags, setSuggestedTags] = useState([]);
  const [keyPoints, setKeyPoints] = useState([]);
  
  // Enhancement state
  const [originalText, setOriginalText] = useState('');
  const [enhancedText, setEnhancedText] = useState('');
  const [enhancementType, setEnhancementType] = useState('improve');
  const [changesMade, setChangesMade] = useState([]);
  
  // Quick phrases state
  const [symptomFilter, setSymptomFilter] = useState('');
  const [quickPhrases, setQuickPhrases] = useState([]);

  const generateSuggestion = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/ai-assist/suggest-note`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          chart_id: chartId,
          note_type: noteType,
          patient_name: patientName,
          diagnosis: diagnosis,
          recent_symptoms: recentSymptoms,
          current_medications: currentMedications,
          visit_type: visitType,
          additional_context: additionalContext
        })
      });
      
      if (!response.ok) throw new Error('Failed to generate suggestion');
      
      const data = await response.json();
      setSuggestedContent(data.suggested_content);
      setSuggestedTitle(data.suggested_title);
      setSuggestedTags(data.suggested_tags);
      setKeyPoints(data.key_points);
      
      toast.success('AI suggestion generated!');
    } catch (error) {
      console.error('Suggestion error:', error);
      toast.error('Failed to generate suggestion');
    } finally {
      setIsLoading(false);
    }
  }, [chartId, noteType, patientName, diagnosis, recentSymptoms, currentMedications, visitType, additionalContext]);

  const enhanceNote = useCallback(async () => {
    if (!originalText.trim()) {
      toast.error('Please enter text to enhance');
      return;
    }
    
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/ai-assist/enhance-note`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          original_text: originalText,
          note_type: noteType,
          enhancement_type: enhancementType
        })
      });
      
      if (!response.ok) throw new Error('Failed to enhance note');
      
      const data = await response.json();
      setEnhancedText(data.enhanced_text);
      setChangesMade(data.changes_made);
      
      toast.success('Note enhanced!');
    } catch (error) {
      console.error('Enhancement error:', error);
      toast.error('Failed to enhance note');
    } finally {
      setIsLoading(false);
    }
  }, [originalText, noteType, enhancementType]);

  const getQuickPhrases = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = new URL(`${API_URL}/api/ai-assist/quick-phrases`);
      url.searchParams.append('note_type', noteType);
      if (symptomFilter) url.searchParams.append('symptom', symptomFilter);
      
      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to get phrases');
      
      const data = await response.json();
      setQuickPhrases(data.phrases);
      
      toast.success('Quick phrases loaded!');
    } catch (error) {
      console.error('Phrases error:', error);
      toast.error('Failed to load phrases');
    } finally {
      setIsLoading(false);
    }
  }, [noteType, symptomFilter]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const useSuggestion = () => {
    if (onNoteGenerated) {
      onNoteGenerated({
        content: suggestedContent,
        title: suggestedTitle,
        tags: suggestedTags,
        note_type: noteType
      });
    }
    setIsOpen(false);
    toast.success('Suggestion applied!');
  };

  return (
    <>
      <Button
        variant={buttonVariant}
        size={buttonSize}
        onClick={() => setIsOpen(true)}
        className={className}
        data-testid="ai-assist-btn"
      >
        <Sparkles className="w-4 h-4 mr-2" />
        AI Assist
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              AI Documentation Assistant
            </DialogTitle>
            <DialogDescription>
              Get AI-powered help with clinical documentation
            </DialogDescription>
          </DialogHeader>

          {/* Tab Navigation */}
          <div className="flex gap-2 border-b border-slate-700 pb-2">
            <Button
              variant={activeTab === 'suggest' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('suggest')}
            >
              <Lightbulb className="w-4 h-4 mr-2" />
              Generate
            </Button>
            <Button
              variant={activeTab === 'enhance' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('enhance')}
            >
              <Wand2 className="w-4 h-4 mr-2" />
              Enhance
            </Button>
            <Button
              variant={activeTab === 'phrases' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('phrases')}
            >
              <FileText className="w-4 h-4 mr-2" />
              Phrases
            </Button>
          </div>

          {/* Generate Tab */}
          {activeTab === 'suggest' && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Note Type</Label>
                  <Select value={noteType} onValueChange={setNoteType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {NOTE_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Visit Type</Label>
                  <Input
                    value={visitType}
                    onChange={(e) => setVisitType(e.target.value)}
                    placeholder="e.g., Routine visit, Follow-up"
                  />
                </div>
              </div>
              
              <div>
                <Label>Additional Context</Label>
                <Textarea
                  value={additionalContext}
                  onChange={(e) => setAdditionalContext(e.target.value)}
                  placeholder="Any specific observations or concerns to include..."
                  rows={3}
                />
              </div>

              {/* Patient Context Display */}
              {(patientName || diagnosis) && (
                <Card className="bg-slate-800">
                  <CardHeader className="py-2">
                    <CardTitle className="text-sm text-slate-400">Patient Context</CardTitle>
                  </CardHeader>
                  <CardContent className="py-2 text-sm">
                    {patientName && <p><strong>Patient:</strong> {patientName}</p>}
                    {diagnosis && <p><strong>Diagnosis:</strong> {diagnosis}</p>}
                    {recentSymptoms.length > 0 && (
                      <p><strong>Symptoms:</strong> {recentSymptoms.join(', ')}</p>
                    )}
                  </CardContent>
                </Card>
              )}
              
              <Button 
                onClick={generateSuggestion} 
                disabled={isLoading}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Suggestion
                  </>
                )}
              </Button>

              {/* Suggestion Result */}
              {suggestedContent && (
                <div className="space-y-4 mt-4">
                  <Card className="bg-slate-800">
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-500" />
                        {suggestedTitle}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-slate-300 whitespace-pre-wrap">{suggestedContent}</p>
                      
                      {suggestedTags.length > 0 && (
                        <div className="flex gap-1 mt-3 flex-wrap">
                          {suggestedTags.map((tag, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {keyPoints.length > 0 && (
                    <Card className="bg-yellow-900/20 border-yellow-600/30">
                      <CardHeader className="py-2">
                        <CardTitle className="text-sm text-yellow-400 flex items-center gap-2">
                          <Lightbulb className="w-4 h-4" />
                          Key Points to Verify
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="py-2">
                        <ul className="text-sm text-slate-300 space-y-1">
                          {keyPoints.map((point, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="text-yellow-400">•</span>
                              {point}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}

                  <div className="flex gap-2">
                    <Button 
                      onClick={useSuggestion}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Use This Suggestion
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => copyToClipboard(suggestedContent)}
                    >
                      <ClipboardCopy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Enhance Tab */}
          {activeTab === 'enhance' && (
            <div className="space-y-4 mt-4">
              <div>
                <Label>Original Text</Label>
                <Textarea
                  value={originalText}
                  onChange={(e) => setOriginalText(e.target.value)}
                  placeholder="Paste your note text here to enhance..."
                  rows={5}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Note Type</Label>
                  <Select value={noteType} onValueChange={setNoteType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {NOTE_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Enhancement Type</Label>
                  <Select value={enhancementType} onValueChange={setEnhancementType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ENHANCEMENT_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <type.icon className="w-4 h-4" />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button 
                onClick={enhanceNote} 
                disabled={isLoading || !originalText.trim()}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enhancing...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4 mr-2" />
                    Enhance Note
                  </>
                )}
              </Button>

              {enhancedText && (
                <div className="space-y-4">
                  <Card className="bg-slate-800">
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-500" />
                        Enhanced Version
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-slate-300 whitespace-pre-wrap">{enhancedText}</p>
                    </CardContent>
                  </Card>

                  {changesMade.length > 0 && (
                    <Card className="bg-blue-900/20 border-blue-600/30">
                      <CardHeader className="py-2">
                        <CardTitle className="text-sm text-blue-400">Changes Made</CardTitle>
                      </CardHeader>
                      <CardContent className="py-2">
                        <ul className="text-sm text-slate-300 space-y-1">
                          {changesMade.map((change, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="text-blue-400">•</span>
                              {change}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}

                  <Button 
                    variant="outline"
                    onClick={() => copyToClipboard(enhancedText)}
                    className="w-full"
                  >
                    <ClipboardCopy className="w-4 h-4 mr-2" />
                    Copy Enhanced Text
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Phrases Tab */}
          {activeTab === 'phrases' && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Note Type</Label>
                  <Select value={noteType} onValueChange={setNoteType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {NOTE_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Symptom (optional)</Label>
                  <Input
                    value={symptomFilter}
                    onChange={(e) => setSymptomFilter(e.target.value)}
                    placeholder="e.g., pain, nausea, anxiety"
                  />
                </div>
              </div>

              <Button 
                onClick={getQuickPhrases} 
                disabled={isLoading}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Get Quick Phrases
                  </>
                )}
              </Button>

              {quickPhrases.length > 0 && (
                <div className="space-y-2">
                  <Label>Click to copy:</Label>
                  {quickPhrases.map((phrase, i) => (
                    <Card 
                      key={i}
                      className="bg-slate-800 cursor-pointer hover:bg-slate-700 transition-colors"
                      onClick={() => copyToClipboard(phrase)}
                    >
                      <CardContent className="py-3 flex items-center justify-between">
                        <p className="text-sm text-slate-300">{phrase}</p>
                        <ClipboardCopy className="w-4 h-4 text-slate-500" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
